import { type FC, useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetWorkflow, useCanEditAutomation, type PlainWorkflow } from "@/queries/automation-queries";
import {
    workflowName,
    workflowsUrl,
    isWorkflowDeleting,
    workflowDetailsEditUrl,
    WorkflowDetailsTab,
    workflowDetailsTabFromLocation,
} from "@/utils/automations";
import { WorkflowExecutionsList } from "@/components/automations/AutomationExecutionsList";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown";
import { AutomationGroupAccess } from "@/components/automations/AutomationGroupAccess";
import { ShareAutomationButton } from "@/components/automations/ShareAutomationButton";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Text } from "@/components/podkit/typography/Text";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { AutomationDefinitionSectionView } from "@/routes/automations/automation-details-page/AutomationDefinitionSection";
import { Button } from "@/components/flexkit/Button";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPencil from "central-icons/IconPencil";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";
import { Banner } from "@/components/flexkit/Banner";
import {
    convertWorkflowActionToFormSteps,
    convertTriggersToFormData,
    convertWorkflowActionToFormLimits,
} from "@/components/automations/automation-edit-form-data";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { SubPageHeader } from "@/components/flexkit/page/SubPageHeader";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { PageContent } from "@/components/flexkit/page/PageContent";
import { Page } from "@/components/flexkit/page/Page";

const IconPencilWithSize = withGitpodSize(IconPencil);

export const AutomationDetailsPage: FC = () => {
    const location = useLocation();
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { data: workflow, error: workflowError } = useGetWorkflow(workflowId);
    const { canEditAutomation } = useCanEditAutomation(workflowId);
    const [selectedTab, setSelectedTab] = useState(
        workflowDetailsTabFromLocation(location) ?? WorkflowDetailsTab.Executions,
    );

    useDocumentTitle(workflow ? workflowName(workflow) : undefined);

    useEffect(() => {
        const fromHash = workflowDetailsTabFromLocation({ hash: location.hash });
        if (fromHash) {
            setSelectedTab(fromHash);
        }
    }, [location.hash]);

    const changeTab = (value: string) => {
        navigate(`#${value}`, { replace: true });
    };

    const isDeleting = workflow ? isWorkflowDeleting(workflow) : false;
    const steps = useMemo(() => convertWorkflowActionToFormSteps(workflow?.spec?.action), [workflow?.spec?.action]);

    return (
        <Page data-testid="workflow-page">
            <SubPageHeader
                breadcrumbs={
                    workflow ? [{ label: "Automations", to: workflowsUrl() }, { label: workflowName(workflow) }] : []
                }
                actions={
                    workflow ? (
                        <AutomationDetailsPageActionButtons workflow={workflow} canUpdate={canEditAutomation} />
                    ) : undefined
                }
                loading={workflow ? undefined : { breadcrumbItemCount: 2 }}
            />
            <PageContent className="gap-4">
                {isDeleting && (
                    <Banner variant="info" text="This automation is being deleted. All interactions are disabled." />
                )}

                <ErrorMessage error={workflowError} />

                {workflow && (
                    <div className="flex flex-col gap-1">
                        <Heading1>{workflowName(workflow)}</Heading1>
                        <Text className="text-base text-content-secondary">
                            {workflow.metadata?.description || "No description provided."}
                        </Text>
                    </div>
                )}

                <Tabs className="flex grow flex-col" value={selectedTab} onValueChange={changeTab}>
                    <TabsList className="mb-6 w-fit flex-row">
                        <TabsTrigger value={WorkflowDetailsTab.Executions}>Executions</TabsTrigger>
                        <TabsTrigger value={WorkflowDetailsTab.Definition}>Definition</TabsTrigger>
                    </TabsList>

                    <TabsContent
                        value={WorkflowDetailsTab.Definition}
                        className="flex flex-col data-[state=active]:grow"
                    >
                        {workflow ? (
                            <AutomationDefinitionSectionView
                                workflowId={workflow.id}
                                trigger={convertTriggersToFormData(workflow.spec?.triggers ?? [])[0]}
                                limits={convertWorkflowActionToFormLimits(workflow.spec?.action)}
                                steps={steps}
                                executor={workflow.metadata?.executor}
                            />
                        ) : null}
                    </TabsContent>

                    <TabsContent
                        value={WorkflowDetailsTab.Executions}
                        className="flex flex-col data-[state=active]:grow"
                    >
                        {workflow ? <WorkflowExecutionsList workflow={workflow} /> : null}
                    </TabsContent>
                </Tabs>
            </PageContent>
        </Page>
    );
};

const AutomationDetailsPageActionButtons: FC<{ workflow: PlainWorkflow; canUpdate: boolean }> = ({
    workflow,
    canUpdate,
}) => {
    const navigate = useNavigate();

    const handleEdit = useCallback(() => {
        navigate(workflowDetailsEditUrl({ id: workflow.id }));
    }, [navigate, workflow.id]);

    return (
        <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 @[660px]/page-header:flex">
                <AutomationGroupAccess workflowId={workflow.id} />
                <ShareAutomationButton workflowId={workflow.id} size="sm" />
                <div className="h-4 w-px bg-border-subtle" />
            </div>
            <WorkflowActionsDropdown workflow={workflow} showRunAction={false} buttonSize="sm" buttonVariant="ghost" />
            <div className="hidden items-center gap-2 @[660px]/page-header:flex">
                {canUpdate ? (
                    <Button
                        variant="secondary"
                        size="sm"
                        LeadingIcon={IconPencilWithSize}
                        onClick={handleEdit}
                        data-tracking-id="edit"
                    />
                ) : null}
            </div>
            <RunAutomationButton workflow={workflow} size="sm" data-tracking-id="run" />
        </div>
    );
};
